/**
 * Objeto que contém as queries disponíveis.
 * Funções que recebem os dados necessários para fazer
 * a query e retornam um objeto pronto para o corpo da
 * requisição
 *
 * Usar o tipo Pick para filtrar parâmetros opcionais.
 * Exemplo `{cpf}: Pick<GQL.IGetProposalDetailsOnQueryArguments, 'cpf'>`
 */
const queries = {
  config: () => ({
    query: `
                query {
                    getSanConfigura {
                        assessoriaPalette
                        corEscura
                        corEscuraSecundaria
                        corClara
                        homePageFontColor
                        homePageLinkColor
                        corDadosUsuario
                        corHeader
                        corFooter
                        assessoriaImage
                        assessoriaName
                        assessoriaLogo
                        urlLogoSecundario
                        imagemCentralHome
                        imagemCentralMobile
                        assessoriaTelephone
                        startBusinessDayAttendance
                        endBusinessDayAttendance
                        startSaturdayAttendance
                        endSaturdayAttendance
                        usaPix
                        usaPagamentoCartaoCredito
                        usaCopiarLD
                        usaSMSLD
                        usaWhatsappLD
                        usaEmailLD
                        usaDownloadBoleto
                        usaImprimirBoleto
                        mensagemBoleto
                        usaReceberLigacao
                        usaAgendarLigacao
                        usaLigarAgora
                        usaWhatsapp
                        usaEnviarMensagem
                        usaEnviarEmail
                        usaDataNascimento
                        useTerms
                        politicaPrivacidade
                        profileOmni
                        tempoExpiracaoToken
                        tituloSite
                        whatsappContact
                        whatsappMessage
                        enviaTokenSMS
                        enviaTokenWhatsapp
                        validacaoAcesso
                        enderecoAssessoria
                        favicon
                        gTagManager
                        usaBordaHomePageCpfInput
                        exibeFaq
                        textoFaq
                        mensagemCpfNaoEncontrado
                        radiusButton
                        nomeFont
                        linkFont
                        corFontFooter
                        radiusCards
                        radiusBotoesContato
                        textoMelhorOpcao
                        emailAssessoria
                    }
                }
            `,
  }),

  validateUser: ({
    hashId,
    campaignId,
  }: GQL.IValidateUserOnQueryArguments) => ({
    query: `
        query {
            validateUser(hashId: "${hashId}", campaignId: "${campaignId}") {
                userCPF userId
                hasToken userPJ
                userName
                creditorId
            }}`,
  }),

  getContacts: ({ cpf }: GQL.IGetTelephoneByCpfOnQueryArguments) => ({
    query: `query {
      getEmail(cpf: "${cpf}") {
        databaseId
        cod_email
        email
      }
      getTelephoneByCpf(cpf: "${cpf}") {
        databaseId
        cod_tel
        ddd_tel
        telephone
      }
    }`,
  }),

  getTelsAndEmails: ({ cpf }: GQL.IGetTelephoneByCpfOnQueryArguments) => ({
    query: `query {
      getEmail(cpf: "${cpf}") {
        databaseId
        cod_email
        email
      }
      getTelephoneByCpf(cpf: "${cpf}") {
        databaseId
        cod_tel
        ddd_tel
        telephone
      }
    }`,
  }),

  getUserNameByCpf: (cpf: string) => ({
    query: `query {
      getUserNameByCpf(cpf: "${cpf}"){
        name
        databaseId
        data
      }
    }`,
  }),

  generateToken: ({
    data: { contactId, contactType, cpf, databaseId },
  }: GQL.IGenerateTokenOnMutationArguments) => ({
    query: `mutation {
      generateToken(data : {
        databaseId: ${databaseId},
        contactId: ${contactId},
        contactType: ${contactType},
        cpf: "${cpf}"
      })
    }`,
  }),

  setEmailConfirmado: (data: GQL.ISetEmailConfirmado) => ({
    query: `mutation {
      setEmailConfirmado(contactId: ${data.contactId}, databaseId: ${data.databaseId})
    }`,
  }),

  setTelefoneConfirmado: (data: GQL.ISetTelefoneConfirmado) => ({
    query: `mutation {
      setTelefoneConfirmado(contactId: ${data.contactId}, databaseId: ${data.databaseId})
    }`,
  }),

  setEnderecoConfirmado: (data: GQL.ISetEnderecoConfirmado) => ({
    query: `mutation {
      setEnderecoConfirmado(
        cep: "${data.cep}",
        number: "${data.number}",
        complement: "${data.complement}",
        neighborhood: "${data.neighborhood}",
        city: "${data.city}",
        state: "${data.state}"
      )
    }`,
  }),

  acceptTerms: () => ({
    query: `mutation { setTermosDataAceite }`,
  }),

  getUpdateDebts: () => ({
    query: `query {
      getUpdatedDebts {
        databaseId
        creditorId
        creditorName
        longerDelay
        updatedValue
        originalValue
        proposalId
        proposalDueDate
        discountPercentage
        proposalType
        contracts,
        contractDetails{
          contract,
          originalContract,
          descriptionProduct 
          originalContractInstallments {
            number
            mainValue
            originalValue
            dueDate
            formattedDueDate
            installmentId,
          }
        }
      }
    }
    `,
  }),

  claimPayment: ({
    options,
    databaseId,
  }: GQL.IClaimPaymentOnMutationArguments) => ({
    query: `mutation {
      claimPayment(
        options: {
          base64: "${options.base64}",
          description: "${options.description}",
          fileExtension: "${options.fileExtension}",
          paymentDate: "${options.paymentDate}",
          contracts: ${JSON.stringify(options.contracts)},
        },
        databaseId: ${databaseId}
      )
    }`,
  }),

  getProposalDetails: ({
    creditorId,
    proposalType,
    proposalId,
  }: GQL.IGetProposalDetailsOnQueryArguments) => ({
    query: `query{
      getProposalDetails(creditorId: ${creditorId}, proposalType: ${proposalType}, proposalId: ${proposalId}){
        plan
        entryValue
        portionValue
      }
    }
    `,
  }),

  getAgreementDetails: () => ({
    query: `query{
      getDealDebt{
        creditorId
        creditorName
        dealId
        plan
        totalPlanValue
        portion
        portionValue
        portionDueDate
        contracts
        databaseId,
        contractDetails{
          contract,
          originalContract,
          descriptionProduct
        },
        status
      }
    }
    `,
  }),

  getWppByCreditorId: ({
    credorDatabaseId,
    creditorId,
  }: GQL.IGetPublicDataByCreditorIdOnQueryArguments) => ({
    query: `query{
      getPublicDataByCreditorId(
        credorDatabaseId: ${credorDatabaseId},
        creditorId: ${creditorId}
      ){
        whatsapp
      }
    }`,
  }),

  getConfirmatedInfoIsRequired: ({
    credorDatabaseId,
    creditorId,
  }: GQL.IGetPublicDataByCreditorIdOnQueryArguments) => ({
    query: `query{
      getPublicDataByCreditorId(
        credorDatabaseId: ${credorDatabaseId},
        creditorId: ${creditorId}
      ){
        confirmaDadosCadastrais
      }
    }`,
  }),

  getIfRegisterIsAllowed: ({
    credorDatabaseId,
    creditorId,
  }: GQL.IGetPublicDataByCreditorIdOnQueryArguments) => ({
    query: `query{
      getPublicDataByCreditorId(
        credorDatabaseId: ${credorDatabaseId},
        creditorId: ${creditorId}
      ){
        permiteCadastro
      }
    }`,
  }),

  sendEmail: ({
    cpf,
    emailFinanciado,
    texto,
    assunto,
    databaseId,
    credorId,
  }: GQL.ISendEmailOnMutationArguments) => ({
    query: `mutation{
      sendEmail(
        emailFinanciado:"${emailFinanciado}",
        cpf:"${cpf}", 
        assunto:"${assunto}",
        texto:"${texto}",
        databaseId:${databaseId},
        credorId:${credorId}
      )
    }
    `,
  }),

  getUserConfirmedData: () => ({
    query: `query{
      getUserConfirmedData{
        telephone
        cep
        city
        complement
        neighborhood
        email
        number
        publicPlace
        state
        ddd_tel
        addressType
      }
    }
    `,
  }),

  getPoliticaPrivacidadeDataAceite: () => ({
    query: `
      query{
        getPoliticaPrivacidadeDataAceite
    }`,
  }),

  setPoliticaPrivacidadeDataAceite: () => ({
    query: `
      mutation{
        setPoliticaPrivacidadeDataAceite
    }`,
  }),

  getAgreementPortionDetails: ({
    dealId,
    databaseId,
  }: GQL.IGetDealPortionsOnQueryArguments) => ({
    query: `query{
      getDealPortions(dealId:${dealId}, databaseId: ${databaseId}){
        portionValue,
        portionNumber,
        portionStatus,
        portionDueDate,
        canEmitBillet
      }
    }`,
  }),

  setUserConfirmedData: (data: GQL.IUserInput) => ({
    query: ` mutation{
      setUserConfirmedData(user:{ 
          telephone:${data.telephone ? `"${data.telephone}"` : null},
          cep:${data.cep ? `"${data.cep}"` : null},
          state:${data.state ? `"${data.state}"` : null},
          publicPlace:${data.publicPlace ? `"${data.publicPlace}"` : null},
          number:${data.number ? `"${data.number}"` : null},
          complement:${data.complement ? `"${data.complement}"` : null},
          neighborhood:${data.neighborhood ? `"${data.neighborhood}"` : null},
          city:${data.city ? `"${data.city}"` : null},
          email:${data.email ? `"${data.email}"` : null},
          ddd_tel:${data.ddd_tel ? `"${data.ddd_tel}"` : null},
          addressType: ${data.addressType || null}
        }){
        cpf
        telephone
        cep
        state
        publicPlace
        number
        complement
        city
        email
        ddd_tel
        addressType
      }
    }`,
  }),

  getBilletProposal: ({
    planId,
    proposalId,
    databaseId,
  }: GQL.IGetBilletProposalOnQueryArguments) => ({
    query: `query{
      getBilletProposal(databaseId: ${databaseId}, planId:${planId}, proposalId:${proposalId})
    }`,
  }),

  getBilletDeal: ({
    dealId,
    portionId,
    databaseId,
  }: GQL.IGetBilletDealOnQueryArguments) => ({
    query: `query{
      getBilletDeal(databaseId: ${databaseId}, dealId:${dealId}, portionId:${portionId}) {
        nnumero
        linhaDigitavel
      }
    }`,
  }),

  getLinhaDigitavelProposalBillet: ({
    // => DEPRECATED
    options: { proposalId, plan, sendType },
    databaseId,
  }: GQL.IGetLinhaDigitavelBilletOnQueryArguments) => ({
    query: `query {
      getLinhaDigitavelBillet(databaseId: ${databaseId}, options: { proposalId: ${proposalId}, sendType: ${sendType}, plan: ${plan} }) {
        nnumero
        linhaDigitavel
      }
    }`,
  }),

  getLinhaDigitavelDealBillet: ({
    options: { dealId, portionId, sendType, creditorId },
    databaseId,
  }: GQL.IGetLinhaDigitavelDealOnQueryArguments) => ({
    query: `query {
      getLinhaDigitavelDeal(databaseId: ${databaseId}, options: { dealId: ${dealId}, sendType: ${sendType}, portionId: ${portionId}, creditorId: ${
      creditorId ?? null
    }}) {
        nnumero
        linhaDigitavel
      }
    }`,
  }),
  generateNewTelephone: (data: GQL.IGenerateTokenInput, phone: string) => ({
    query: ` mutation{
      generateTokenToNewPhone(data: {
        cpf: "${data.cpf}",
        contactType: ${data.contactType},
        databaseId: ${data.databaseId},
        contactId: ${data.contactId}
      }, phone:"${phone}")
    }`,
  }),
  validateNewTelephone: ({ token, ddd, phone }: ValidateNewTelephone) => ({
    query: ` mutation {
      validateTokenAndUpdatePhone(
      phone: "${phone}"
      token: "${token}",
      ddd: "${ddd}"
      ){
        telephone
        ddd_tel
      }
    }`,
  }),

  getNegotiationDetail: ({
    contract,
    creditorId,
    databaseId,
    paymentType,
    installmentId,
    dealId,
  }: GQL.IGetDealOptionsOnQueryArguments) => ({
    query: `
      query {
        getDealOptions(contract: ${JSON.stringify(
          contract
        )}, creditorId: ${creditorId}, databaseId: ${databaseId}, ${
      paymentType !== undefined ? `paymentType: ${paymentType}` : ''
    },${
      dealId
        ? `agreementId:${dealId}`
        : `installmentId: ${JSON.stringify(installmentId)}`
    }
    ) {
          optionId
          dueDate
          formatedDueDate
          firstPortionValue
          plan
          totalValue
          honorarioValue
          othersPortionsValue
          othersPortionsDueDate
          othersPortionsFormatedDueDate
          negotiationType
          negotiationTypeDescription
          maxDiscountValue
          allowsOptionsRecalculation
          isBestInstallment
      		paymentMethod
      		discountPercentage
          allowProjectionDate
          }
        }
     `,
  }),

  getDealState: ({
    dealId,
    contract,
    databaseId,
  }: GQL.IGetDealStateOnQueryArguments) => ({
    query: `
        query {
              getDealState(databaseId: ${databaseId}, dealId: ${dealId}, contract: ${JSON.stringify(
      contract
    )}){
              id,
              creditorId,
              creditorStatus,
              status,
              statusDescription,
              formatedDueDate
              }
            }
       `,
  }),

  confirmNegotiation: ({
    options,
    databaseId,
  }: GQL.IConfirmNegotiationOnQueryArguments) => ({
    query: `
    query {
      confirmNegotiation(
        databaseId: ${databaseId},
        options: {
          contrato: ${JSON.stringify(options.contrato)},
          opcaoID: "${options.opcaoID}"
        }
      ) {
        enderecos {
          logradouro
          numero
          complemento
          bairro
          cidade
          cep
          uf
          tipo
          idExterno
          codigo
        }
        telefones {
          codigo
          ddd
          numero
        }
        emails {
          codigoEmail
          enderecoEmail
        }
        questionamentos {
          questao
          respostas {
            chave
            valor
          }
      }
    }
  }
    `,
  }),

  getDebtDetails: ({
    options,
    databaseId,
  }: GQL.IGetDebtDetailsOnQueryArguments) => ({
    query: `
    query {
      getDebtDetails(
        databaseId: ${databaseId},
        options: {
          contrato: "${options.contrato}",
          opcaoID: "${options.opcaoID}"
        }
      ) {
        textoConfirmacao
    }
  }
    `,
  }),

  setDeal: ({
    options: { contrato, opcaoID, valorEntrada, valorDemais },
    databaseId,
    dealId,
  }: GQL.ISetDealOnQueryArguments) => ({
    query: `
      query{
        setDeal(databaseId: ${databaseId}, options:{
          contrato: ${JSON.stringify(contrato)},
          opcaoID: "${opcaoID}",
          valorEntrada: ${valorEntrada},
          valorDemais: ${valorDemais}
          ${dealId ? `, reparcelamento: ${true}` : ''}
        }){
          status
          statusDescription,
          creditorStatus,
          id,
          creditorId,
          agreementValue
        }
      }
     `,
  }),

  setCall: ({ options }: GQL.ISetCallOnMutationArguments) => ({
    query: `mutation {
  setCall(
    options: {
      ddd_tel: "${options.ddd_tel}",
      telephone: "${options.telephone}",
      dataHora: "${options.dataHora /* 2021-02-15T10:51:39 */}",
      campanha : "${options.campanha}"
    }
  )
}`,
  }),

  getBillet: ({ id, databaseId }: GQL.IGetBilletOnQueryArguments) => ({
    query: `
      query{
        getBillet(databaseId: ${databaseId}, id: "${id}"){
          status
          statusText
          data
        }
      }
     `,
  }),

  rpaGetLinhaDigBillet: ({
    id,
    databaseId,
  }: GQL.IGetBilletOnQueryArguments) => ({
    query: `
      query{
        rpaGetLinhaDigBillet(databaseId: ${databaseId}, id: "${id}"){
          status
          statusText
          data
        }
      }
     `,
  }),

  rpaGetMaskedCpf: ({ id, databaseId }: GQL.IGetBilletOnQueryArguments) => ({
    query: `
      query{
        getMaskedCpf(guid: "${id}", databaseId: ${databaseId})
      }
    `,
  }),

  rpaIsValidCpf: ({
    id,
    databaseId,
    partialCpf,
  }: GQL.IGetValidCpfOnQueryArguments) => ({
    query: `
      query{
        cpfIsValid(guid: "${id}", databaseId: ${databaseId}, partialCpf: "${partialCpf}")
      }
    `,
  }),

  getPixData: ({
    options: { dealId, portionId },
    databaseId,
  }: GQL.IGetPixDataOnQueryArguments) => ({
    query: `
      query{
        getPixData(options: {dealId: ${dealId} , portionId:${portionId}},databaseId: ${databaseId}){
          numeroPix
          linhaDigitavel
          valorPago
          valorPagoFormatado
        }
      }
     `,
  }),

  getIframePayment: ({
    dealId,
    portionId,
    databaseId,
  }: GQL.IGetIframePaymentOnQueryArguments) => ({
    query: `
      query{
        getIframePayment(dealId: ${dealId}, portionId:${portionId},databaseId: ${databaseId}){
          link
        }
      }
     `,
  }),

  getTermDeal: ({
    options: { dealId, creditorId, contract },
    databaseId,
  }: GQL.IGetTermDealOnQueryArguments) => ({
    query: `
      query {
        getTermDeal(dealId: ${dealId}, creditorId: ${creditorId}, databaseId: ${databaseId}, contract: ["${contract}"])
      }
     `,
  }),

  rpaGetListBilletByCpf: ({ cpf }: GQL.IGetListBilletByCpf) => ({
    query: `
    query{
      rpaGetListBilletByCpf(cpf: "${cpf}" ){
        value
        id
        originalContract
        issueDate
        dueDate
        contract
        databaseId
        billetText
      }
    }
    `,
  }),

  getPosNegotiationMessage: ({
    creditorId,
    credorDatabaseId,
  }: GQL.IGetPublicDataByCreditorIdOnQueryArguments) => ({
    query: `
    query{
      getPublicDataByCreditorId (creditorId: ${creditorId},  credorDatabaseId: ${credorDatabaseId} ){
        mensagemPosNegociacao
        confirmaDadosCadastrais
      }
    }
    `,
  }),

  validateCreditCard: ({
    databaseId,
    options,
  }: GQL.IValidateCreditCardOnMutationArguments) => ({
    query: `
    mutation{ 
      validateCreditCard(
        databaseId: ${databaseId},
        options: {
          operator: ${options.operator},
          cardData: {
            expirationMonth: "${options.cardData.expirationMonth}",
            expirationYear: "${options.cardData.expirationYear}",
            installmentPlan: "${options.cardData.installmentPlan}",
            name: "${options.cardData.name}",
            number: "${options.cardData.number}",
            securityCode: "${options.cardData.securityCode}"
          }
        }
      ) {
        id
        message
      }
    }
    `,
  }),
  confirmCreditCardPayment: ({
    databaseId,
    options,
  }: GQL.IConfirmCreditCardPaymentOnMutationArguments) => ({
    query: `
    mutation{ 
      confirmCreditCardPayment(
        databaseId: ${databaseId},
        options: {
          operator: ${options.operator},
          agreementId: ${options.agreementId},
          installmentNumber: ${options.installmentNumber},
          cardData: {
            expirationMonth: "${options.cardData.expirationMonth}",
            expirationYear: "${options.cardData.expirationYear}",
            installmentPlan: "${options.cardData.installmentPlan}",
            name: "${options.cardData.name}",
            number: "${options.cardData.number}",
            securityCode: "${options.cardData.securityCode}"
          }
        }
      )
    }
    `,
  }),

  deleteDeal: ({
    databaseId,
    agreementId,
    motivo,
  }: GQL.IDeleteDealOnMutationArguments) => ({
    query: `
    mutation{ 
      deleteDeal(
        databaseId: ${databaseId},
        idAcordo: ${agreementId},
        motivo: "${motivo}"
      ) {
        idAcordo
        motivo  
      }
    }
    `,
  }),

  sendIframeLog: ({
    databaseId,
    responseCode,
    status,
    details,
    auxiliary,
    agreementId,
    finContract,
  }: GQL.ISendIframeLogOnMutationArguments) => ({
    query: `
    mutation{ 
      sendIframeLog(
        databaseId: ${databaseId},
        responseCode: "${responseCode}",
        status: "${status}",
        details: "${details}",
        auxiliary: "${auxiliary}",
        agreementId: "${agreementId}",
        finContract: "${finContract}"
        
      ) {
        message
      }
    }
    `,
  }),
};

export default queries;
